// Static content used across pages.
import {
  Globe, ShoppingBag, Search, Bot, Cpu, Smartphone, Sparkles, Wrench,
} from "lucide-react";

export const SERVICES = [
  {
    slug: "website-design",
    icon: Globe,
    title: "Website Design & Development",
    short: "Premium websites designed to convert visitors into customers.",
    long: "Conversion-focused, fast-loading, on-brand websites built on modern tech with SEO and automation baked in.",
    benefits: [
      "Bespoke premium design tuned to your brand",
      "Mobile-first, lightning fast, accessible",
      "SEO foundations and analytics from day one",
      "Lead capture and CRM automation included",
    ],
    features: [
      "Custom design system",
      "CMS-ready content blocks",
      "Schema markup & SEO",
      "Form + WhatsApp + Email routing",
      "Performance budget < 1s LCP",
    ],
    process: [
      { title: "Strategy", text: "Goals, audience, journeys, success metrics." },
      { title: "Design", text: "Premium visual direction and prototypes." },
      { title: "Build", text: "Production-grade development on modern stack." },
      { title: "Launch & Grow", text: "Go-live, analytics, optimisation." },
    ],
    tech: ["React", "Next.js", "Tailwind", "Framer Motion", "Webflow", "Headless CMS"],
    pricing: "From $199",
    packages: [
      { id: "wd-starter", name: "Starter Website", price: 1499, mode: "payment", desc: "5-page premium website, fast launch." },
      { id: "wd-growth", name: "Growth Website", price: 3499, mode: "payment", desc: "12-page site with AI chat + CMS + automation." },
      { id: "wd-premium", name: "Premium AI Growth Website", price: null, mode: "quote", desc: "Bespoke AI growth engine — request proposal." },
    ],
  },
  {
    slug: "ecommerce-development",
    icon: ShoppingBag,
    title: "Ecommerce Development",
    short: "Online stores built for sales, automation, and growth.",
    long: "Stores engineered to convert browsers into buyers — with smart cart recovery, AI recommendations, and connected operations.",
    benefits: [
      "Designed for AOV and conversion uplift",
      "Abandoned cart automation built in",
      "AI product recommendations",
      "Connected to your fulfilment & accounting",
    ],
    features: ["Shopify / Custom builds", "Smart checkout flows", "Multi-currency, multi-region", "Analytics & funnel insight", "Inventory & order automation"],
    process: [
      { title: "Audit", text: "Funnel and store readiness review." },
      { title: "Design", text: "Premium product storytelling and UX." },
      { title: "Build", text: "Theme + integrations + automation." },
      { title: "Scale", text: "Ongoing CRO and feed optimisation." },
    ],
    tech: ["Shopify", "Next.js Commerce", "Klaviyo", "Stripe"],
    pricing: "From $999",
    packages: [
      { id: "ec-starter", name: "Ecommerce Starter", price: 3499, mode: "payment", desc: "Premium Shopify or custom store launch." },
      { id: "ec-growth", name: "Ecommerce Growth", price: 6499, mode: "payment", desc: "Advanced CRO, automation, multi-currency." },
      { id: "ec-enterprise", name: "Enterprise Ecommerce", price: null, mode: "quote", desc: "Headless commerce, custom scale." },
    ],
  },
  {
    slug: "seo-ai-search-optimization",
    icon: Search,
    title: "SEO & AI Search Optimization",
    short: "Rank higher on Google and become visible in AI search engines.",
    long: "We help you win classic Google SEO and the new AI search era — ChatGPT, Perplexity, Gemini, Google AI Overviews.",
    benefits: ["Higher Google rankings for high-intent terms", "Citations inside AI answers", "Technical, on-page, off-page covered", "Transparent monthly reporting"],
    features: ["Keyword + AI intent research", "Technical SEO audit", "Content production", "Authority link building", "Schema & entity SEO"],
    process: [
      { title: "Discovery", text: "Audit, competitors, opportunity map." },
      { title: "Foundations", text: "Technical fixes + content architecture." },
      { title: "Growth", text: "Content, links, AI citations." },
      { title: "Report", text: "Monthly transparent reporting." },
    ],
    tech: ["Ahrefs", "Search Console", "Schema.org", "Perplexity", "ChatGPT Search"],
    pricing: "From $199/month",
    packages: [
      { id: "seo-starter", name: "Monthly SEO Starter", price: 499, mode: "payment", desc: "Foundational SEO + monthly reporting." },
      { id: "seo-growth", name: "Growth SEO", price: 999, mode: "payment", desc: "Content + links + AI search optimisation." },
      { id: "seo-custom", name: "Custom SEO", price: null, mode: "quote", desc: "Bespoke enterprise SEO programme." },
    ],
  },
  {
    slug: "ai-automation",
    icon: Bot,
    title: "AI Automation",
    short: "Automate repetitive tasks and save your business hours every week.",
    long: "We build practical AI workflows that handle quoting, follow-ups, customer support, and reporting — so your team focuses on what matters.",
    benefits: ["Save 10+ hours per week", "Faster response times for customers", "Fewer errors, more consistency", "Plays nicely with your existing tools"],
    features: ["Lead → CRM automation", "AI email follow-ups", "Auto quoting & proposals", "AI customer support agents", "Slack / WhatsApp / Email triggers"],
    process: [
      { title: "Map", text: "Identify the highest-value workflows." },
      { title: "Design", text: "Sketch the automation, approve flow." },
      { title: "Build", text: "Implement with safe rollouts." },
      { title: "Iterate", text: "Measure hours saved, expand." },
    ],
    tech: ["n8n", "Make", "OpenAI", "Claude", "Zapier"],
    pricing: "From $999",
    packages: [
      { id: "auto-starter", name: "Automation Starter", price: 999, mode: "payment", desc: "One key workflow automated end-to-end." },
      { id: "auto-business", name: "Business Automation", price: 2999, mode: "payment", desc: "Five workflows + AI agents + integrations." },
      { id: "auto-enterprise", name: "Enterprise Automation", price: null, mode: "quote", desc: "Custom AI automation programme." },
    ],
  },
  {
    slug: "saas-development",
    icon: Cpu,
    title: "SaaS Development",
    short: "Build scalable software platforms and digital products.",
    long: "From MVP to enterprise platform — we ship SaaS products people love to use, on time, on budget.",
    benefits: ["Senior product engineering", "Modern, scalable architecture", "Built-in analytics & billing", "Speed to market with quality"],
    features: ["Auth & multi-tenant", "Billing (Stripe)", "Dashboards & reporting", "API + integrations", "AI features ready"],
    process: [
      { title: "Strategy", text: "Product, users, monetisation." },
      { title: "Prototype", text: "Clickable design + technical plan." },
      { title: "MVP", text: "Ship in weeks, not quarters." },
      { title: "Scale", text: "Iterate based on real usage." },
    ],
    tech: ["React", "FastAPI", "Postgres", "MongoDB", "Stripe", "AWS"],
    pricing: "Custom Quote",
    packages: [
      { id: "saas-mvp", name: "SaaS MVP", price: null, mode: "quote", desc: "MVP in 6–10 weeks. Discovery call required." },
      { id: "saas-platform", name: "SaaS Platform", price: null, mode: "quote", desc: "Full product engagement, multi-quarter." },
    ],
  },
  {
    slug: "mobile-app-development",
    icon: Smartphone,
    title: "Mobile App Development",
    short: "Create powerful mobile experiences.",
    long: "Native-feel iOS and Android apps that delight users and drive business outcomes.",
    benefits: ["Cross-platform reach with one codebase", "Polished, native-quality experience", "Push, payments, offline ready", "Connected to your existing systems"],
    features: ["iOS + Android", "Push notifications", "Auth, payments, deep links", "Offline-first patterns", "App Store deployment"],
    process: [
      { title: "Strategy", text: "User journeys + tech approach." },
      { title: "Design", text: "Native interaction patterns." },
      { title: "Build", text: "React Native / Swift / Kotlin." },
      { title: "Launch", text: "Store submission and monitoring." },
    ],
    tech: ["React Native", "Expo", "Swift", "Kotlin"],
    pricing: "From $6,500",
    packages: [
      { id: "app-mvp", name: "Mobile MVP", price: 6500, mode: "payment", desc: "Cross-platform MVP, store deployment." },
      { id: "app-pro", name: "Pro Mobile App", price: 14500, mode: "payment", desc: "Premium app with payments + automation." },
      { id: "app-enterprise", name: "Enterprise App", price: null, mode: "quote", desc: "Custom native + backend engagement." },
    ],
  },
  {
    slug: "branding",
    icon: Sparkles,
    title: "Branding",
    short: "Create brands customers trust.",
    long: "Identities, messaging, and design systems that elevate your business and command premium pricing.",
    benefits: ["Memorable, premium brand identity", "Clear messaging that converts", "Cohesive across every touchpoint", "Built for long-term scale"],
    features: ["Logo & visual identity", "Brand strategy & messaging", "Design system & guidelines", "Pitch decks & collateral"],
    process: [
      { title: "Discovery", text: "Audience, market, positioning." },
      { title: "Direction", text: "Mood, type, palette." },
      { title: "System", text: "Logo, components, guidelines." },
      { title: "Activate", text: "Roll-out across the business." },
    ],
    tech: ["Figma", "Brand voice frameworks"],
    pricing: "From $2,499",
    packages: [
      { id: "br-essential", name: "Essential Brand", price: 2499, mode: "payment", desc: "Logo + colour + type + mini guidelines." },
      { id: "br-premium", name: "Premium Brand System", price: 5499, mode: "payment", desc: "Full identity, messaging, design system." },
    ],
  },
  {
    slug: "website-maintenance",
    icon: Wrench,
    title: "Website Maintenance",
    short: "Keep your digital assets secure and performing.",
    long: "Proactive monitoring, security, performance, and content updates — so your site keeps earning while you sleep.",
    benefits: ["Uptime & performance monitoring", "Security patches and backups", "Monthly content updates", "Friendly US & UK support"],
    features: ["24/7 uptime monitoring", "Weekly backups", "Security & WAF", "Plugin / dependency updates", "Content updates"],
    process: [
      { title: "Onboard", text: "Audit and baseline." },
      { title: "Stabilise", text: "Patch, optimise, document." },
      { title: "Maintain", text: "Monthly retainer + reporting." },
      { title: "Improve", text: "Quarterly enhancement plan." },
    ],
    tech: ["Cloudflare", "UptimeRobot", "GitHub Actions"],
    pricing: "From $199/month",
    packages: [
      { id: "maint-essential", name: "Maintenance Essential", price: 199, mode: "payment", desc: "Monitoring, backups, security patches." },
      { id: "maint-pro", name: "Maintenance Pro", price: 499, mode: "payment", desc: "Above + monthly content + CRO tweaks." },
    ],
  },
];

// Testimonials are now loaded from MongoDB via /api/testimonials.
// The Testimonials component renders empty-state when no verified testimonials exist.
// Fake / unverifiable testimonials have been removed (Batch 2, Feb 2026).
export const TESTIMONIALS = [];


export const INDUSTRIES = [
  {
    slug: "construction",
    title: "Construction",
    promise: "Generate more enquiries from homeowners and commercial clients.",
    image: "https://images.unsplash.com/photo-1508450859948-4e04fabaa4ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwY29uc3RydWN0aW9uJTIwbWFudWZhY3R1cmluZ3xlbnwwfHx8fDE3ODE4MTgyMjF8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "automotive",
    title: "Automotive",
    promise: "Sell more vehicles and services online.",
    image: "https://images.pexels.com/photos/20123591/pexels-photo-20123591.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "manufacturing",
    title: "Manufacturing",
    promise: "Generate B2B opportunities and qualify leads automatically.",
    image: "https://images.pexels.com/photos/5851494/pexels-photo-5851494.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "trades",
    title: "Trades",
    promise: "Look premium, book more jobs, charge what you're worth.",
    image: "https://images.unsplash.com/photo-1589939705384-5185137a7f0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwY29uc3RydWN0aW9uJTIwbWFudWZhY3R1cmluZ3xlbnwwfHx8fDE3ODE4MTgyMjF8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "professional-services",
    title: "Professional Services",
    promise: "Build authority and convert high-value clients.",
    image: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjB0ZWFtJTIwbWVldGluZyUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3ODE4MTgyMjF8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "saas-startups",
    title: "SaaS & Startups",
    promise: "Launch faster with premium product UX and scalable tech.",
    image: "https://images.unsplash.com/photo-1677442135703-1787eea5ce01?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxmdXR1cmlzdGljJTIwYXJ0aWZpY2lhbCUyMGludGVsbGlnZW5jZSUyMHNlcnZlcnxlbnwwfHx8fDE3ODE4MTgyMjl8MA&ixlib=rb-4.1.0&q=85",
  },
];

// Case studies are now loaded from MongoDB via /api/case-studies.
// The CaseStudies page renders empty-state when no verified projects exist.
// Fake / unverifiable case studies have been removed (Batch 2, Feb 2026).
export const CASE_STUDIES = [];

export const PRICING_TIERS = [
  {
    name: "Starter Website",
    price: "£499",
    cadence: "one-time",
    description: "A premium website that wins your first leads.",
    highlight: false,
    features: [
      "Up to 5 pages",
      "Custom design",
      "SEO foundations",
      "Lead capture form",
      "Mobile + accessibility",
      "2 weeks delivery",
    ],
  },
  {
    name: "Growth Website",
    price: "£999",
    cadence: "one-time",
    description: "For ambitious SMEs serious about growth.",
    highlight: true,
    features: [
      "Up to 12 pages",
      "Premium custom design",
      "Advanced SEO + schema",
      "CRM + WhatsApp + Email automation",
      "Blog + CMS",
      "AI chat assistant",
      "3 weeks delivery",
    ],
  },
  {
    name: "Premium AI Growth System",
    price: "£1,999",
    cadence: "starting at",
    description: "Website + SEO + Automation working together.",
    highlight: false,
    features: [
      "Everything in Growth",
      "AI Search Optimization",
      "Custom automation workflows",
      "Monthly growth retainer",
      "Conversion rate optimisation",
      "Quarterly strategic reviews",
    ],
  },
  {
    name: "Custom SaaS",
    price: "Custom Quote",
    cadence: "",
    description: "MVP → enterprise platforms. Built for scale.",
    highlight: false,
    features: [
      "Discovery & product strategy",
      "Design + full-stack engineering",
      "Auth, billing, dashboards",
      "AI features included",
      "Senior team, transparent process",
    ],
  },
];

export const PROCESS = [
  { title: "Strategy", text: "Understand your business goals and audience." },
  { title: "Design", text: "Create your premium digital experience." },
  { title: "Development", text: "Build with modern technology and AI." },
  { title: "Launch & Growth", text: "SEO, automation, optimisation, support." },
];

export const REASONS = [
  { title: "AI-Powered Growth", text: "We don't just build websites. We build digital growth systems." },
  { title: "Fast Delivery", text: "Launch faster without compromising quality." },
  { title: "SEO Ready From Day One", text: "Every website is designed to rank and convert." },
  { title: "US & UK Support", text: "Work with a reliable digital partner with offices in Nebraska and England." },
  { title: "Fixed Pricing", text: "Clear packages without unexpected costs." },
  { title: "Enterprise Quality", text: "Senior team, premium output, every time." },
];

// Brand logos on the trusted-marquee are now loaded from /api/case-studies
// (only real, verified client names appear). Fabricated marquee logos removed
// (Batch 2, Feb 2026).
export const BRAND_LOGOS = [];
